/*
 * English resource — SOURCE OF TRUTH for the V1.9.1 shell string keys.
 * Scope: NEW shell chrome strings only (Phase 0). Existing screens keep their
 * inline strings until their own restyle phase migrates them here.
 *
 * Per PRODUCT-RULES.md §Localisation: strings live in a resource file per
 * locale — never inline constants ported from the prototype markup.
 */
export const en = {
  // Bottom tab bar / sidebar nav labels
  'nav.getReady': 'Get ready',
  'nav.orders': 'Orders',
  'nav.tickets': 'Tickets',
  'nav.profile': 'Profile',
  'nav.help': 'Help',

  // Top bar controls
  'topbar.help.tip': 'Help and contact support',
  'topbar.lang.tip': 'Language — English',
  // Label on the language toggle = the language it switches TO, written in
  // that language (standard language-switcher convention).
  'topbar.lang.toggleLabel': 'العربية',
  'topbar.account.tip': 'Your account',
  // {name} is interpolated with the real account holder's name.
  'topbar.account.tipNamed': '{name} — your account',

  // Get ready (V1.9.1 Phase 1) — NEW copy introduced by the redesign only;
  // pre-existing screen strings migrate in a later phase.
  // "You're up to date" band (nothing actionable but not finished — handoff
  // README §"Three states, not two"). The derived "Next: {step} — {when}."
  // line is appended after the body by the screen.
  'getReady.upToDate.title': "You're up to date",
  'getReady.upToDate.body': 'Nothing for you to do right now.',
  // Terminal action on the system Ticket-issued row when everything is
  // complete — reads Issued and routes to Tickets (handoff README §Get ready).
  'getReady.issued.action': 'Issued',

  // -------------------------------------------------------------------------
  // Get ready (V1.9.1 Phase 5) — the P1 gap closed: screen chrome + the step
  // label/meta strings migrated OUT of src/data/getReady.ts (they were the
  // last inline-English screen model). Values are the canon-verbatim strings.
  // -------------------------------------------------------------------------
  'getReady.head.title': 'Get ready',
  'getReady.head.sub': 'Do what you can now — the rest unlocks on its own date.',
  // Mono block label (uppercase via the string itself, as measured).
  'getReady.stepsLabel': 'YOUR STEPS FOR THIS EVENT',
  'getReady.card.done': '{done} of {yours} done',
  'getReady.card.ended': 'Event ended',
  // Status pill (canon §4.2 verbatim templates).
  'getReady.pill.todo.one': '1 thing to do',
  'getReady.pill.todo.other': '{n} things to do',
  'getReady.pill.caughtUp': 'Nothing to do right now',
  'getReady.pill.allSet': "You're all set",
  // Next-up line — first not-done step; `{when}` derives from the step meta.
  'getReady.next.withWhen': 'Next: {label} — {when}.',
  'getReady.next.bare': 'Next: {label}.',
  'getReady.note.heatIssuance': 'Your ticket is issued once you have chosen your heat.',
  // ALLOCATION-mode variant (heat_sel=false / system-allocated): the organiser
  // assigns the start time, so the participant never picks a heat — the note
  // must not say "once you have chosen your heat". Selected by heatIssuanceNote
  // off the heat row's `system` flag; mirrors the seed-demo allocation wording.
  'getReady.note.heatIssuance.alloc': 'Your ticket is issued once heats are allocated.',
  'getReady.allSet.title': "You're all set",
  'getReady.allSet.body': 'Your ticket is in your wallet.',
  'getReady.allSet.cta': 'View it in Tickets',
  // Not-yet mono tag boxes (README §Get ready step rows).
  // Card subject label — on EVERY Get-ready card since Duncan's UI order
  // BOARDROOM-135429 (was child-seat-only): child seats show the wire-named
  // child; every other holding shows the signed-in holder's OWN name from the
  // session claims. Rendered only when a subject is NAMED (honest omission
  // otherwise — no name on an older my-api row + a nameless token).
  'getReady.card.forName': 'For {name}',
  'getReady.tag.soon.desktop': 'SCHEDULED',
  'getReady.tag.soon.mobile': 'SOON',
  'getReady.tag.system.desktop': 'WE DO THIS',
  'getReady.tag.system.mobile': 'AUTO',
  'getReady.tag.ended.desktop': 'EVENT ENDED',
  'getReady.tag.ended.mobile': 'ENDED',
  'getReady.meta.closed': 'Event ended — this step is closed',
  // Step CTA labels, keyed by the GetReadyStepCta state-machine value (the
  // enum value is a stable code identifier; the label is locale copy).
  'getReady.cta.Start': 'Start',
  'getReady.cta.Resume': 'Resume',
  'getReady.cta.Sign': 'Sign',
  'getReady.cta.Add': 'Add',
  'getReady.cta.Edit': 'Edit',
  'getReady.cta.View': 'View',
  'getReady.cta.Choose': 'Choose',
  // Step labels + metas (src/data/getReady.ts stepsFromTicketReadiness /
  // questionnaireStepFromStatus).
  'getReady.step.profile.label': 'Your profile',
  'getReady.step.profile.done': 'Completed',
  'getReady.step.profile.pending': 'Details missing',
  'getReady.step.waiver.label': 'Sign your waiver',
  'getReady.step.waiver.done': 'Signed',
  'getReady.step.waiver.optional': 'Optional — you can sign anytime',
  'getReady.step.waiver.required': 'Required by {org}',
  'getReady.step.waiver.requiredNoOrg': 'Required by the organiser',
  'getReady.step.heat.label': 'Heat selection',
  'getReady.step.heat.chosenTime': 'Chosen · {time}',
  'getReady.step.heat.chosen': 'Chosen',
  'getReady.step.heat.pending': 'Choose your start time',
  // box-office mode (P1 BOARDROOM-2026-09-09-171256): the buyer picked the
  // heat at CHECKOUT — reflect it, never "we assign" / never a Choose CTA.
  'getReady.step.heat.checkoutTime': 'Chosen at checkout · {time}',
  'getReady.step.heat.checkout': 'Chosen at checkout',
  // Unstamped box-office unit (BOARDROOM-2026-09-10-132427 honesty polish):
  // nothing was chosen at checkout (empty heat schedule) — say so, openly.
  'getReady.step.heat.boxPending': 'Heat pending',
  'getReady.step.heatAlloc.label': 'Heat allocation',
  'getReady.step.heatAlloc.done': 'Allocated',
  'getReady.step.heatAlloc.pending': 'We assign your start time',
  // A start time is already KNOWN (the wallet wave) under the allocation row —
  // "we assign" would be false; state the time (P1 BOARDROOM-171256).
  'getReady.step.heatAlloc.set': 'Start time · {time}',
  'getReady.step.ticket.label': 'Ticket issued',
  'getReady.step.ticket.done': 'Issued',
  'getReady.step.ticket.pending': 'After you pick a heat',
  // ALLOCATION-mode variant (system-allocated heats): the participant never
  // picks a heat, so the issuance-pending meta reads "After heats are
  // allocated". Selected in stepsFromTicketReadiness by the heatPolicy.
  'getReady.step.ticket.pending.alloc': 'After heats are allocated',
  // BOX-OFFICE variant: the heat is already set at checkout — neither
  // "pick a heat" nor "heats are allocated" is true.
  'getReady.step.ticket.pending.box': 'After your remaining steps are complete',
  'getReady.step.questionnaire.label': 'Event questionnaire',
  'getReady.step.questionnaire.done': 'Completed',
  'getReady.step.questionnaire.pending': 'A few quick questions from the organiser',
  // Guard states (honest, non-conflated — audit D7).
  'getReady.signin.prompt': 'Please sign in to see your event steps.',
  'getReady.loading': 'Loading your events…',
  'getReady.unavailable': 'The get-ready service isn’t responding right now — try again shortly.',
  // Parent-signing visibility (Duncan ruling BOARDROOM-2026-09-02-150714 item
  // 5): the buyer's read-only card for a seat farmed to the child's own
  // parent. Copy tracks the allocations wire's honest holder.state ONLY:
  // invited = not yet claimed; claimed = accepted, in the parent's hands
  // (waiver/profile completeness is NOT on the buyer's wire — never implied);
  // done = the wire's ready/consumed carry. {parent} = the recorded parent
  // name, else their email; the NoName variants cover a record with neither.
  'getReady.waiting.tag': 'PARENT SIGNS',
  'getReady.waiting.invited': 'Waiting for {parent} to accept and sign',
  'getReady.waiting.invitedNoName': 'Waiting for the parent to accept and sign',
  'getReady.waiting.claimed': 'In {parent}’s hands — they’ve accepted and complete the profile and waiver themselves',
  'getReady.waiting.claimedNoName': 'In the parent’s hands — they’ve accepted and complete the profile and waiver themselves',
  'getReady.waiting.done': '{parent} has completed everything — this place is all set',
  'getReady.waiting.doneNoName': 'The parent has completed everything — this place is all set',
  // Removed-holdings "Recent changes" breadcrumb (build item #6, BOARDROOM-
  // 2026-09-05-121056; review fixes F2/F4/F5): ONE shared `recentChanges.*`
  // namespace — the note renders only in the ORDERS empty state now (the
  // Get-ready empty branch is the CMT-034 redirect to Orders again, F4), but
  // the namespace stays screen-neutral so a future surface reuses it. Shown
  // ONLY when the /me/holdings wire's additive removedHoldings[] is present
  // and non-empty. PRIVACY DEFAULT: actor TYPE only, recipient identity never
  // rendered ("another participant"). {head} = "{event} — {ticket type}" (or
  // the event alone), built in RemovedHoldingsNote; the *NoDate variants are
  // the honest date-less lines. COPY HONESTY (F2): owner lines are change-
  // verb-aware — only a transfer-shaped change claims a transfer; a revoked/
  // unknown owner change reads "removed"; a null/unknown actor gets the
  // actor-SILENT `unknown` variant, never "updated automatically".
  'recentChanges.title': 'Recent changes',
  'recentChanges.owner': '{head} — transferred to another participant on {date} by the order owner',
  'recentChanges.ownerNoDate': '{head} — transferred to another participant by the order owner',
  'recentChanges.ownerRemoved': '{head} — removed by the order owner on {date}',
  'recentChanges.ownerRemovedNoDate': '{head} — removed by the order owner',
  'recentChanges.support': '{head} — changed by customer support on {date}',
  'recentChanges.supportNoDate': '{head} — changed by customer support',
  'recentChanges.system': '{head} — updated automatically on {date}',
  'recentChanges.systemNoDate': '{head} — updated automatically',
  'recentChanges.unknown': '{head} — changed on {date}',
  'recentChanges.unknownNoDate': '{head} — changed',
  'recentChanges.contactSupport': 'Contact support',

  // -------------------------------------------------------------------------
  // Orders (V1.9.1 Phase 2) — handoff README §Screens → 2. Orders.
  // `{hole}` params are interpolated by i18n/useLocale interpolate().
  // -------------------------------------------------------------------------
  'orders.head.title': 'Orders',
  'orders.head.sub': 'Every place in your orders, grouped by ticket type.',
  'orders.card.eyebrow': 'ORDER',
  // Chevron glyph appended in code — it swaps direction under RTL. Label per
  // the invoice-slot commission (BOARDROOM-2026-09-01-175306 item 4); the
  // link renders ONLY when invoiceUrlForOrder resolves a real artefact.
  'orders.card.invoice': 'View invoice',
  'orders.card.ticketsLabel': 'TICKETS IN THIS ORDER',
  // Pluralisation follows the count exactly (QA §5).
  'orders.summary.tickets.one': '1 ticket',
  'orders.summary.tickets.other': '{n} tickets',
  'orders.summary.need.one': '1 needs action',
  'orders.summary.need.other': '{n} need action',
  'orders.summary.need.mobile': '{n} to do',
  'orders.summary.done.desktop': '{n} complete',
  'orders.summary.done.mobile': '{n} done',
  'orders.summary.assigned.one': '{done} of {total} place assigned',
  'orders.summary.assigned.other': '{done} of {total} places assigned',
  'orders.block.people.one': '1 place',
  'orders.block.people.other': '{n} places',
  'orders.block.toAssign': '{n} to assign',
  // Team-member block label (BOARDROOM-2026-09-09-161918; Duncan naming
  // ruling: "Ticket" — matches the "{n} tickets" header, never "Person").
  'orders.block.unitTicket': '{type} - Ticket {n}',
  'orders.block.waiting': 'Waiting to accept',
  'orders.block.complete': 'Complete',
  'orders.row.unassigned': 'Unassigned',
  'orders.row.assignNow': 'Assign now',
  'orders.row.invitedTag': 'INVITED',
  'orders.row.seatTag': 'Place {n}',
  'orders.row.editTitle': 'Edit this place',
  // Local-record sub-lines (child/guardian modes — client state, TODO WIRE).
  'orders.row.subGuestparent': 'Parent invited for {child} — waiting for them to complete it',
  'orders.row.subGuestparentNoName': 'Parent invited — waiting for them to complete it',
  // CLAIMED phase (parent-signing visibility, BOARDROOM-2026-09-02-150714
  // item 5): the server holder has accepted — honest wire state only, no
  // implied waiver/profile completion. {parent} = recorded name, else email.
  'orders.row.subGuestparentClaimed': '{parent} has accepted — they complete the profile and waiver themselves',
  'orders.row.subGuestparentClaimedNoName': 'The parent has accepted — they complete the profile and waiver themselves',
  'orders.row.subTeen': 'Under 18 · you sign as guardian',
  'orders.row.subTeenLogin': 'Under 18 · you sign as guardian · view-only login sent to {email}',
  'orders.row.subGuestchild': "Someone else's child — you sign on their parent's behalf",
  'orders.row.subChild': 'On your account — you complete their details',
  // Row-state notices (server-signalled; never invented).
  'orders.row.subEnded': 'Event ended — this place can no longer be assigned',
  'orders.row.subNoSeat': 'Can’t be assigned yet — this place isn’t set up yet',
  // Allocation guard states (honest, state-accurate — audit D7). Customer
  // language only (BOARDROOM-2026-09-01-193406): no "allocation service" /
  // "seat assignments" jargon anywhere customer-visible. The unavailable copy
  // shows only after the retry/backoff sequence in useOrderSeats exhausts.
  'orders.signin.prompt': 'Please sign in to see your tickets.',
  'orders.signin.cta': 'Sign in',
  'orders.alloc.loading': 'Loading your tickets…',
  'orders.alloc.unavailable': 'We’re having trouble loading your tickets — this usually resolves in a moment. Refresh to try again.',
  'orders.alloc.unconfigured': 'Ticket details are not connected in this build yet — they appear the moment they are.',
  // Current/Past partition (BOARDROOM-2026-09-01-175306 items 2–3; grace per
  // MY-2026-09-01-180752): the collapsed read-only archive + the no-current
  // empty state with its browse link.
  'orders.past.title': 'Past events',
  'orders.empty.current': 'You have no current orders.',
  'orders.empty.browse': 'Browse events',
  // Empty-state copy triplet (build item #6 + review fix F5): the "yet" claim
  // is EVIDENCE-GATED — `none` (the optimistic "yet" line, the pre-item-#6
  // hardcoded copy moved into the resource) renders only once the get-ready
  // live context is READY and reports no removed holdings; `removed` renders
  // when it reports some (with the recentChanges.* note); `neutral` is the
  // no-claim line shown while the context is still loading/unavailable —
  // asserting "yet" (or a removal) before the evidence lands would be a guess.
  'orders.empty.none': 'No orders are connected to this account yet. Orders you place appear here with every ticket and place inside them.',
  'orders.empty.neutral': 'No orders are connected to this account.',
  'orders.empty.removed': 'No orders are connected to this account. Orders you place appear here with every ticket and place inside them.',

  // -------------------------------------------------------------------------
  // Assign flow (V1.9.1 Phase 2) — handoff README §The flows → A. Assign a place.
  // -------------------------------------------------------------------------
  // Shared flow chrome
  'flow.back': 'Back',
  'flow.continue': 'Continue',
  // Accessible name of the flow-header back/close chevron button.
  'flow.close': 'Close',
  'flow.tapToContinue': 'Tap an option to continue',
  'flow.step': 'Step {n} of {m}',
  'flow.assigned': 'ASSIGNED',
  'flow.backToOrder': 'Back to the order',
  'flow.getReadyNow': 'Get ready now',

  // Step eyebrows (mono, uppercase via CSS)
  'assign.eyebrow.ticket': 'Ticket {n} of {m}',
  'assign.eyebrow.ticketPerson': 'Ticket {n} of {m} · Place {x} of {y}',
  'assign.eyebrow.bracket': 'Their age',
  'assign.eyebrow.relation': 'Whose teenager',
  'assign.eyebrow.permission': 'Who signs the waiver',
  'assign.eyebrow.details': 'Their details',
  'assign.eyebrow.confirm': 'Check and confirm',

  // Step 1 — who
  'assign.aged': 'Aged {min}–{max}',
  'assign.question.who': 'Who is taking this ticket?',
  'assign.question.whoReassign': 'Who should hold this ticket now?',
  'assign.reassignNote': 'This place is currently held by {who}. Choosing someone else replaces them.',
  // REPLACE banner on the one-step parent-send entry page (Duncan refinement
  // BOARDROOM-2026-09-05-120713): CONCRETE names, never roles — {current} is
  // the standing allocation's holder label, {next} the live-typed child name
  // or parent email as available ('…' until one exists).
  'assign.replaceBanner': "Confirm: you're replacing {current} with {next}",
  'assign.choice.me': 'Myself — {name}',
  'assign.choice.other': 'Someone else',
  'assign.choice.child': 'My child',
  'assign.choice.guest': "Someone else's child",

  // Age bracket step (the bracket is a QUESTION, never a date of birth)
  'assign.bracket.heading': 'How old will they be on race day?',
  'assign.bracket.intro': 'Anyone under {age} cannot sign their own waiver, so we handle it differently.',
  'assign.bracket.adult.title': '{age} or over',
  'assign.bracket.adult.note': 'They sign their own waiver and manage their own place.',
  'assign.bracket.adult.tag': 'SIGNS FOR THEMSELF',
  'assign.bracket.minor.title': 'Under {age}',
  'assign.bracket.minor.note': 'A parent or guardian has to sign the waiver for them.',
  'assign.bracket.minor.tag': 'NEEDS A GUARDIAN',

  // Whose-teenager step (plain A-or-B — no tags, no sub-notes)
  'assign.relation.heading': 'Whose teenager is this?',
  'assign.relation.intro': 'This decides who signs the waiver for them.',
  'assign.relation.mine': 'This teenager is my child.',
  'assign.relation.other': "This teenager is someone else's child.",

  // Who-signs step
  'assign.permission.heading': 'Who signs their waiver?',
  'assign.permission.intro':
    "A child's waiver can only be signed by their parent or guardian, or by someone the parent has authorised.",
  'assign.perm.have.title':
    "I have the parent's permission to sign the waiver for this child. I will sign it now.",
  'assign.perm.have.note':
    "You add the child's details and sign the waiver on the parent's behalf. They join your account under someone else's children.",
  'assign.perm.have.tag': 'YOU SIGN',
  'assign.perm.parent.title':
    "I do NOT have permission. I will enter the parent's email and they will sign themselves.",
  'assign.perm.parent.note': 'We invite them to complete the profile and sign. Nothing further for you to do.',
  'assign.perm.parent.tag': 'PARENT SIGNS',

  // Details step — headings + intros
  'assign.details.heading.parentEmail': "Their parent's email",
  'assign.details.heading.addChild': 'Add the child',
  'assign.details.heading.addTeen': 'Add the teenager',
  'assign.details.heading.whichChild': 'Which child?',
  'assign.details.heading.whichTeen': 'Which teenager?',
  'assign.details.heading.invite': 'Where do we send the invite?',
  'assign.details.intro.guestParent':
    "We'll email them to complete the profile and sign. The place is held in the meantime.",
  'assign.details.intro.guestHave':
    "You're signing on their parent's behalf, so they sit under someone else's children on your account.",
  'assign.details.intro.child': 'You complete their details and sign the waiver for them.',
  'assign.details.intro.invite': "They'll get an email invite and complete their own profile and waiver.",

  // Details step — fields
  'assign.field.parentName.label': "Parent's name",
  'assign.field.optionalSuffix': '(optional)',
  'assign.field.parentName.hintIdle': 'Only used to address the email — leave blank if you don’t know it',
  'assign.field.parentName.hintNamed': 'We’ll write “Dear {name}”',
  'assign.field.parentEmail.label': "Parent or guardian's email",
  'assign.field.email.label': 'Their email',
  'assign.field.parentEmail.hintIdle': 'They get the waiver and complete the profile',
  'assign.field.email.hintIdle': 'They claim the ticket with this address',
  'assign.field.email.ok': '✓ Looks right',
  'assign.field.email.bad': 'That does not look like an email address',
  'assign.field.childName.label': "Child's name",
  'assign.field.name.label': 'Their name',
  'assign.field.name.placeholder': 'So you can tell places apart',
  'assign.field.firstName': 'First name',
  'assign.field.lastName': 'Last name',
  'assign.field.dob': 'Date of birth',
  'assign.field.gender': 'Gender',
  'assign.field.dob.day': 'Day',
  'assign.field.dob.month': 'Month',
  'assign.field.dob.year': 'Year',

  // Add-a-child / picker
  'assign.addChild.rest': 'Nationality, kit and emergency contact come with their profile.',
  'assign.addChild.noneOwnChild': "You haven't added anyone yet.",
  'assign.addChild.noneGuestChild': "You haven't signed for anyone else's child yet.",
  'assign.addChild.noneGuestTeen': "You haven't signed for anyone else's teenager yet.",
  'assign.addChild.addChildLabel': 'Add a child',
  'assign.addChild.addTeenLabel': 'Add a teenager',
  'assign.addChild.switchChild': 'Choose an existing child instead',
  'assign.addChild.switchTeen': 'Choose an existing teenager instead',
  'assign.addChild.ageOn': 'Age {age} on race day',
  'assign.kidAge.bad': "{age} on race day — outside this ticket's {min}–{max} range.",
  'assign.kidAge.ok': '✓ {age} on race day — eligible for {ticket}',
  'assign.kidAge.okNoBand': '✓ {age} on race day',

  // Teen view-only access card (my-teenager route only, on the chosen teen)
  'assign.teen.title': 'Does {name} need their own access?',
  'assign.teen.titleUnnamed': 'Does this teenager need their own access?',
  'assign.teen.body':
    'If you want {name} to see their own ticket, enter their email address below. They will not be able to sign their own waiver or change their details — those stay under your control.',
  'assign.teen.bodyUnnamed':
    'If you want them to see their own ticket, enter their email address below. They will not be able to sign their own waiver or change their details — those stay under your control.',
  'assign.teen.label': 'Their email',
  'assign.teen.hintIdle': 'Leave blank if they do not need their own access',

  // Blocked-reason bar (specific, ordered to match field order — QA §9)
  'assign.block.chooseChild': 'Choose which child',
  'assign.block.chooseTeen': 'Choose which teenager',
  'assign.block.childNames': "Enter the child's first and last name",
  'assign.block.teenNames': "Enter the teenager's first and last name",
  'assign.block.childDob': "Enter the child's date of birth",
  'assign.block.teenDob': "Enter the teenager's date of birth",
  'assign.block.band': 'They must be {min}–{max} on race day for this ticket',
  'assign.block.childGender': "Choose the child's gender",
  'assign.block.teenGender': "Choose the teenager's gender",
  // Child/teen gender-mismatch blocks (BOARDROOM-2026-09-02-053243 finding 3)
  // — same voice as the self-assign genderEligible reason.
  'assign.block.gender.child.male': "This ticket is for male participants; the child's recorded gender doesn't match. Contact the organiser if this is wrong.",
  'assign.block.gender.child.female': "This ticket is for female participants; the child's recorded gender doesn't match. Contact the organiser if this is wrong.",
  'assign.block.gender.teen.male': "This ticket is for male participants; the teenager's recorded gender doesn't match. Contact the organiser if this is wrong.",
  'assign.block.gender.teen.female': "This ticket is for female participants; the teenager's recorded gender doesn't match. Contact the organiser if this is wrong.",
  // ADD-path MISSING-gender blocks (quality P0 launch-blocker 2026-09-02): on
  // a gender-restricted ticket a blank gender is INELIGIBLE — the copy says
  // WHY the normally-optional field must be answered here.
  'assign.block.genderMissing.child.male': "This ticket is restricted to male participants — select the child's gender.",
  'assign.block.genderMissing.child.female': "This ticket is restricted to female participants — select the child's gender.",
  'assign.block.genderMissing.teen.male': "This ticket is restricted to male participants — select the teenager's gender.",
  'assign.block.genderMissing.teen.female': "This ticket is restricted to female participants — select the teenager's gender.",
  'assign.block.teenEmail': 'Enter their email address for the view-only login',
  'assign.block.parentEmail': "Enter the parent's email address",
  'assign.block.email': 'Enter a valid email address',

  // Confirm step — headings, rows, next notes, button labels
  'assign.confirm.heading.me': 'Confirm this is yours',
  'assign.confirm.heading.child': 'Confirm this place',
  'assign.confirm.heading.send': 'Ready to send',
  'assign.confirm.intro.me': 'This place will use your existing profile.',
  'assign.confirm.intro.teenChild': 'You sign the waiver for them.',
  'assign.confirm.intro.child': 'No invite is sent — you complete their details yourself.',
  'assign.confirm.intro.other': 'Nothing is sent until you confirm.',
  'assign.row.ticket': 'Ticket {n} of {m}',
  'assign.row.ticketPerson': 'Ticket {n} of {m} · place {x} of {y}',
  'assign.row.assignedTo': 'Assigned to',
  'assign.row.parent': 'Parent',
  'assign.row.age': 'Age',
  'assign.row.age.value': 'Under {age}',
  'assign.row.waiver': 'Waiver & payment',
  'assign.row.waiver.value': '{name} (guardian)',
  'assign.row.teenLogin': 'Their view-only login',
  'assign.row.parentsChild': "Parent's child",
  'assign.next.me': 'The ticket moves to your Get ready list.',
  'assign.next.guestHave': "You'll fill in their profile and sign their waiver on the parent's behalf.",
  'assign.next.guestParent': 'We email the parent now. The place is held until they complete it — nothing further for you.',
  'assign.next.child': "You'll fill in their profile and sign their waiver.",
  'assign.next.teenLogin': 'You sign their waiver. They get a view-only login to see their ticket.',
  'assign.next.teenNoLogin': 'You sign their waiver and hold the ticket. Nothing for them to do.',
  'assign.next.other': 'They get an email now. Until they accept, you can resend or reassign.',
  // One-step parent send (BOARDROOM-2026-09-05-120305 item 5): the parent-
  // email ENTRY step is the send step — this CTA fires the invite (the old
  // separate review page is unrouted on the guestparent path).
  'assign.cta.confirmSend': 'Confirm & send invite',
  'assign.cta.signLater': 'Confirm & sign later',
  'assign.cta.sendInvite': 'Send invite',
  'assign.cta.confirm': 'Confirm',
  'assign.cta.sending': 'Sending…',

  // Field placeholders (format illustrations only — never a person's name:
  // prototype names must not ship, PRODUCT-RULES §Names are data).
  'assign.field.email.placeholder': 'name@example.com',
  'assign.teen.placeholder': 'them@example.com',

  // Submit / entry error + guard states (honest; real Error messages surface
  // verbatim, these are the fallbacks).
  'assign.error.gender': 'This ticket is restricted and your profile gender does not match.',
  // Duplicate participant within ONE team unit (CMT-2026-08-29-026 /
  // BOARDROOM-2026-08-29-114614): fallback copy for the server's 422 AND the
  // client-side replace pre-check (adversarial-review P1) — the server's own
  // `message` surfaces verbatim when present. References the EVENT, not "this
  // ticket" (adversarial-review P2 copy nit): post cross-ticket fan-out
  // (CMT-2026-08-29-027) sibling places of one unit render as SEPARATE ticket
  // cards, so the copy must read true in both shapes.
  'assign.error.duplicateParticipant': 'This person already holds another place for this event — each place needs a different person.',
  // NON-BLOCKING cross-unit note (same dispatch): the address already appears
  // on ANOTHER ticket of this event in the order — allowed (multi-heat days
  // are legitimate), just worth a heads-up. Exact ruling copy.
  'assign.warn.sameEventElsewhere': 'Please make sure you have enough time to attend both.',
  'assign.error.failed': 'The assignment could not be completed.',
  'assign.error.liveHolder': 'This place already has a live holder — open it from the ticket’s Manage view to replace them.',
  // Never coach a revoke (BOARDROOM-2026-08-17-210222): direct to the open
  // sibling place when one exists.
  'assign.error.liveHolderSibling': 'This place already has a live holder. Place {n} on this ticket is still unassigned — assign that place instead.',
  // Customer language (BOARDROOM-2026-09-01-193406): no "allocation service"
  // jargon in anything customer-visible.
  'assign.error.service': 'Something went wrong on our side — please try again.',
  'assign.entry.eventEnded': 'This event has ended — places can no longer be assigned or changed.',
  'assign.entry.loading': 'Loading this place…',
  'assign.entry.noSeat': 'This place can’t be assigned just yet — please check back in a moment.',
  'assign.entry.cantOpen': 'This place can’t be opened for assigning right now — please try again in a moment.',

  // Done screen
  'assign.done.reassigned': 'Place reassigned',
  'assign.done.parentInvited': 'Parent invited',
  'assign.done.reserved': 'Place reserved',
  'assign.done.inviteSent': 'Invite sent',
  'assign.done.ticketAssigned': 'Ticket assigned',
  'assign.done.assignedYou': 'Assigned to you',
  'assign.done.sub.guestParent':
    "We've emailed {who} at {email} to complete the profile and sign the waiver. The place is held until they do.",
  'assign.done.sub.guestParentNotSent':
    '{who} holds this place, but the invite email couldn’t be sent yet — you can resend it from the order.',
  'assign.done.sub.guestHave':
    "{name} is on your account under someone else's children. Finish their details in Profile → {name}.",
  'assign.done.sub.teen': '{name} holds this place. You sign their waiver in Get ready.',
  'assign.done.sub.teenLogin':
    "{name} holds this place. You sign their waiver in Get ready, and we've sent a view-only login to {email}.",
  'assign.done.sub.other': "We've emailed {who}. You'll see it here as soon as they accept.",
  'assign.done.sub.otherNotSent':
    '{who} now holds this place, but the invite email couldn’t be sent yet — you can resend it from the order.',
  'assign.done.sub.child': '{name} is on your account and holds this place. Finish their details in Profile → {name}.',
  'assign.done.sub.me': 'This place is yours. It now appears in Get ready.',
  // Wrong-address escape on the parent-invite done screen (BOARDROOM-2026-09-
  // 05-120305 item 5): returns to the prefilled entry step; the resend rides
  // the verified revoke-then-rotate mechanics (re-assign revokes the prior
  // invite first — the old link is invalidated server-side).
  'assign.done.changeResend': 'Wrong address? Change & resend',

  // -------------------------------------------------------------------------
  // Tickets (V1.9.1 Phase 3) — handoff README §Screens → 3. Tickets.
  // -------------------------------------------------------------------------
  'tickets.head.title': 'Tickets',
  'tickets.head.sub': 'Issued tickets only — one at a time, so you scan the right one. Works offline.',
  'tickets.pager.prev': 'Previous ticket',
  'tickets.pager.next': 'Next ticket',
  // Mono position line (uppercase via CSS).
  'tickets.pager.pos': 'TICKET {i} of {n}',
  'tickets.stub.admitOne': 'ADMIT ONE',
  'tickets.stub.participant': 'Participant',
  'tickets.stub.heat': 'Heat',
  'tickets.stub.bib': 'Bib',
  'tickets.stub.gate': 'Gate',
  'tickets.stub.date': 'Date',
  'tickets.stub.scanAtGate': 'SCAN AT THE GATE',
  'tickets.stub.qrAlt': 'Entry QR code',
  'tickets.qr.loading': 'Checking your entry code…',
  'tickets.qr.notReady': 'Your entry QR appears here once get-ready (profile, waiver, heat) is complete.',
  'tickets.qr.unavailable': 'The QR service isn’t responding right now — try again shortly.',
  'tickets.qr.unconfigured': 'The QR service is not connected in this build — nothing here is fabricated.',
  'tickets.ended.badge': 'Event ended',
  'tickets.empty': 'No issued tickets yet. A ticket appears here the moment every get-ready step for its event is done.',
  'tickets.pending.kicker': 'NOT ISSUED YET',
  'tickets.pending.blocked.one': '1 thing still to do before we can issue this',
  'tickets.pending.blocked.other': '{n} things still to do before we can issue this',
  'tickets.pending.waiting': 'All done — we issue it at heat allocation ({when})',
  'tickets.pending.ended': 'This event has ended — this ticket is no longer issuable.',
  'tickets.pending.cta': 'Get ready',
  'tickets.signin.prompt': 'Please sign in to see your tickets.',
  'tickets.signin.cta': 'Sign in',
  'tickets.signin.busy': 'Opening sign-in…',
  'tickets.loading': 'Loading your tickets…',
  'tickets.unavailable': 'The ticket service isn’t responding right now — try again shortly.',

  // -------------------------------------------------------------------------
  // Profile section (V1.9.1 Phase 3) — handoff README §Screens → 4. Profile.
  // -------------------------------------------------------------------------
  'profile.head.title': 'Profile',
  'profile.account.fallbackName': 'Your account',
  'profile.account.edit': 'Edit',
  'profile.managed.kicker': 'Managed profiles',
  'profile.managed.guestKicker': "Someone else's children",
  'profile.managed.empty': 'No one yet. Add a child to enter them into junior events — they never need their own login.',
  // Child-row note fragments — joined with ` · `; nothing renders when there
  // is nothing worth saying (README §Screens 4).
  'profile.child.age': 'Age {n}',
  'profile.child.complete': 'profile complete',
  'profile.child.toFinish': 'profile to finish',
  'profile.addChild.open': '+ Add a child',
  'profile.addChild.title': 'Add a child',
  'profile.addChild.sub': 'Nationality, kit and emergency contact come with their first event.',
  'profile.addChild.first': 'First name',
  'profile.addChild.last': 'Last name',
  'profile.addChild.dob': 'Date of birth',
  'profile.addChild.gender': 'Gender',
  'profile.addChild.cancel': 'Cancel',
  'profile.addChild.save': 'Add child',
  // Account edit hub (/profile/edit — CMT-2026-09-03-026): the summary-card
  // hub Edit opens on a completed profile. Reuses the wizard summary rows +
  // the Profile grouping kickers; only hub chrome copy lives here. Arabic
  // arrives via the ar.ts spread as UNTRANSLATED placeholders pending a
  // translator + native reviewer (PRODUCT-RULES §Localisation).
  'profile.hub.back': 'Profile',
  'profile.hub.title': 'Your details',
  'profile.hub.sub': 'Tap a pencil to change one thing — you’ll come straight back here.',
  'profile.hub.kicker.you': 'Your profile',
  'profile.hub.editRow': 'Edit {field}',
  'profile.hub.loading': 'Loading your details…',
  'profile.hub.loadFail': 'Couldn’t load your saved details — check your connection and try again.',
  // Distinct signed-out copy (adversarial review F5): a null profile read
  // means NO SESSION — the connection-failure copy would be a lie there.
  'profile.hub.signedOut': 'You’re signed out — sign in again to see and edit your details.',
  'profile.hub.retry': 'Try again',
  'profile.settings.kicker': 'Account',
  'profile.settings.password': 'Password',
  'profile.settings.password.meta': 'Change',
  'profile.settings.twoStep': 'Two-step verification',
  'profile.settings.notifications': 'Notifications',
  'profile.settings.signOut': 'Sign out',

  // Child-row note fragments (joined with ` · `):
  'profile.child.fallbackName': 'Child',
  // Signing-status flag on EVERY "someone else's children" entry — exactly
  // three explicit states, one vocabulary (Duncan ruling BOARDROOM-2026-09-
  // 02-150714 item 4, ratified). Supersedes the old unconditional
  // `profile.child.guestClause` sub-line ("you sign on their parent's
  // behalf"), which contradicted the parent-signs state.
  'profile.signing.label': 'Signing:',
  'profile.signing.you': 'you sign for them',
  'profile.signing.parent': 'their parent signs for them',
  'profile.signing.undetermined': '(not yet determined)',
  // THIRD child grouping — "deferred — sent to their parent to sign" (Duncan
  // ruling BOARDROOM-2026-09-02-152856 part C, structural): entries whose
  // signing duty is farmed to the child's own parent live in their OWN titled
  // group, never as a flag state inside the someone-else's group. The sub-line
  // names the parent it went to (typed name, else their email, else an honest
  // generic — never a fabricated name).
  'profile.managed.deferredKicker': 'Deferred — sent to their parent to sign',
  'profile.deferred.sentTo': 'sent to {parentName}',
  'profile.deferred.sentToParent': 'sent to their parent',
  // Password panel (REAL Cognito forgot/confirm primitives — the only wired
  // settings destination besides Sign out; no dead fake settings).
  'profile.password.intro': "We'll email a verification code to {email}.",
  'profile.password.send': 'Email me a code',
  'profile.password.sending': 'Sending…',
  'profile.password.code': 'Verification code',
  'profile.password.new': 'New password',
  'profile.password.confirm': 'Change password',
  'profile.password.changing': 'Changing…',
  'profile.password.done': 'Password changed. Use it next time you sign in.',
  'profile.password.close': 'Close',
  'profile.settings.notAvailable': 'Not available yet',
  // Error fallbacks (thrown non-Errors; real Cognito Error messages surface
  // verbatim, incl. the password-policy text).
  'profile.password.sendFailed': 'could not send the code',
  'profile.password.changeFailed': 'password change failed',
  'profile.loadFailed': 'profile load failed',

  // -------------------------------------------------------------------------
  // Profile flow (V1.9.1 Phase 3) — handoff README §The flows → B. Profile.
  // Step chrome reuses flow.* keys; SAVED replaces the step counter on the
  // receipt (README §Flows B). {name} = the SUBJECT (a managed child) —
  // step 2+ carries it in the header so a parent filling in for several
  // children always knows which one.
  // -------------------------------------------------------------------------
  'wizard.saved': 'SAVED',
  'wizard.context.account': 'Account profile',
  'wizard.newProfile': 'New profile',
  'wizard.save.self': 'Save profile',
  'wizard.save.child': "Save {name}'s profile",
  'wizard.saving': 'Saving…',
  'wizard.done.backToGetReady': 'Back to Get ready',
  'wizard.done.backToProfile': 'Back to Profile',
  // Eyebrows (mono, uppercase via CSS)
  'wizard.eyebrow.personal': 'Personal details',
  'wizard.eyebrow.natres': 'Nationality & residence',
  'wizard.eyebrow.ec': 'Emergency contact',
  'wizard.eyebrow.prefs': 'Preferences',
  'wizard.eyebrow.child.details': "{name}'s details",
  'wizard.eyebrow.child.natres': "{name}'s nationality & residence",
  'wizard.eyebrow.child.kit': "{name}'s kit",
  // Step 1 — name
  'wizard.step1.heading.self': "What's your name?",
  'wizard.step1.heading.child': "What's {name}'s full name?",
  'wizard.step1.intro.self': 'This goes on your bib and your results.',
  // GUARDIAN entry variant (BOARDROOM-2026-09-03-052111 / CMT-2026-09-03-020):
  // the parent claimed a CHILD's seat — their own name is ACCOUNT-HOLDER data,
  // never bib/results copy (Duncan's live-walk defect: the parent was asked
  // for their name under "This goes on your bib and your results").
  'wizard.step1.intro.selfGuardian': "Your name as the account holder — your child's race details come next.",
  'wizard.step1.intro.child': 'It goes on their bib and their results — use the name on their ID or passport.',
  'wizard.field.first': 'First name',
  'wizard.field.last': 'Last name',
  // Step 2 — DOB + gender
  'wizard.field.dob': 'Date of birth',
  'wizard.help.dob': "Used for age categories and results. Pick day, month and year separately so there's no DD/MM confusion.",
  'wizard.dob.echo': "That's {date}",
  'wizard.field.gender': 'Gender',
  'wizard.help.gender.binary': 'Set by {org} for results and age categories.',
  'wizard.help.gender.third': '{org} publishes three results tables — Women, Men and Open.',
  'wizard.selfDescribe.label': 'How do you describe your gender?',
  'wizard.selfDescribe.placeholder': 'In your own words',
  'wizard.gender.flagNotice': "We'll record this as given. Please note {org} ranks results in the Women's and Men's tables only, so a self-described entry won't appear in the published results.",
  'wizard.gender.openNotice.pre': "You'll be timed and ranked in the ",
  'wizard.gender.openNotice.post': " results table, which {org} publishes alongside Women's and Men's.",
  // Step 3 — nationality & residence
  'wizard.nat.label': 'Nationality',
  'wizard.nat.placeholder': 'e.g. British, United Kingdom or GBR',
  'wizard.nat.hintIdle': 'Type a nationality, country or ISO code, then choose from the list',
  'wizard.nat.hintOk': '✓ {dem} — {country} ({iso3})',
  'wizard.res.heading.self': 'Where you live',
  'wizard.res.heading.child': 'Where {name} lives',
  'wizard.res.label': 'Country of residence',
  'wizard.res.placeholder': 'e.g. United Arab Emirates or ARE',
  'wizard.res.hintIdle': 'Type a country or ISO code, then choose from the list',
  'wizard.res.hintOk': '✓ {country} ({iso3})',
  'wizard.res.sameAsNat': 'I live in {country}',
  'wizard.prefill.sameAsMine': 'Same as mine — {value}',
  'wizard.city.label': 'City',
  'wizard.city.placeholder': 'Type to search cities',
  'wizard.city.hintOk': '✓ {city}',
  'wizard.city.hintPick': 'Choose a city in {country}',
  'wizard.city.hintLocked': 'Pick your country of residence first',
  'wizard.city.use': 'Use',
  'wizard.addr.label': 'Street address',
  'wizard.addr.labelOptional': 'Street address (optional)',
  'wizard.phone.label': 'Your phone',
  'wizard.phone.labelOptional': 'Your phone (optional)',
  'wizard.phone.stripped': 'Leading 0 removed — numbers are stored international',
  // Step 4 — emergency contact
  'wizard.ec.intro.self': 'Who should we call in an emergency?',
  'wizard.ec.intro.child': 'Who should we call about {name} on race day?',
  'wizard.help.ec': 'Shared only with event medical staff on race day, and only if something happens to you.',
  'wizard.ec.name': 'Contact name',
  'wizard.ec.phone': 'Contact phone',
  'wizard.ec.rel': 'Relationship',
  'wizard.ec.useMe': 'Use me — {name}',
  // Step 5 — preferences
  'wizard.lang.label': 'Language',
  'wizard.lang.note': 'Trumin will show in this language.',
  'wizard.lang.inherited': '{name} will appear in your account language ({lang}).',
  'wizard.shirt.self': 'T-shirt size',
  'wizard.shirt.junior': 'Junior T-shirt size',
  'wizard.shirt.note': "Most events include a race tee — we'll reuse this.",
  'wizard.shirt.suggested': "Suggested from {name}'s age ({age}). Change it if they need a different size.",
  'wizard.shirt.overridden': "You've chosen {chosen} — the usual size for age {age} is {suggested}.",
  // Done / receipt
  'wizard.done.complete.self': 'Profile complete',
  'wizard.done.complete.child': '{name} is all set',
  'wizard.done.partial': 'Progress saved',
  'wizard.done.sub.complete.self': "Saved to your Trumin account — you won't need to enter this again for future events.",
  'wizard.done.sub.complete.child': "Saved under your account. {name}'s details carry over to their next event — you only sign the waiver each time.",
  'wizard.done.sub.partial.self': 'Saved so far. A few required details are still missing before your ticket can be issued.',
  'wizard.done.sub.partial.child': "Saved so far. A few required details are still missing before {name}'s ticket can be issued.",
  'wizard.done.still': 'Still needed: {list}',
  'wizard.done.addNow': 'Add these now',
  // Blocked-reason phrases (QA §9.7 — the exact missing fields, grammatical,
  // ordered to match the on-screen field order; child variants per README).
  'wizard.need.first': 'your first name',
  'wizard.need.last': 'your last name',
  'wizard.need.dob.self': 'your date of birth',
  'wizard.need.dob.child': 'the date of birth',
  'wizard.need.gender.self': 'your gender',
  'wizard.need.gender.child': 'a category',
  'wizard.need.nationality': 'your nationality',
  'wizard.need.residence': 'your country of residence',
  'wizard.need.city': 'your city',
  'wizard.need.phoneValid': 'a valid phone number',
  'wizard.need.phone': 'your phone number',
  'wizard.need.address': 'your street address',
  'wizard.need.ecName': 'a contact name',
  'wizard.need.ecPhone': 'a valid contact phone',
  'wizard.need.language': 'your language',
  'wizard.need.shirt.self': 'your T-shirt size',
  'wizard.need.shirt.child': 'a T-shirt size',
  'wizard.need.fitness': 'your fitness level',
  'wizard.block.one': 'Add {item} to continue',
  'wizard.block.many': 'Still needed: {list} and {last}',
  // Still-needed list items on the receipt (derived completion)
  'wizard.missing.name': 'name',
  'wizard.missing.dob': 'date of birth',
  'wizard.missing.gender': 'gender',
  'wizard.missing.nationality': 'nationality',
  'wizard.missing.residence': 'country of residence',
  'wizard.missing.city': 'city',
  // Summary card row labels
  'wizard.summary.name': 'Name',
  'wizard.summary.dob': 'Date of birth',
  'wizard.summary.gender': 'Gender',
  'wizard.summary.genderIdentity': 'Gender identity',
  'wizard.summary.nationality': 'Nationality',
  'wizard.summary.residence': 'Residence',
  'wizard.summary.address': 'Address',
  'wizard.summary.phone': 'Phone',
  'wizard.summary.phoneNotGiven': 'Not given',
  'wizard.summary.ec': 'Emergency contact',
  'wizard.summary.ecPhone': 'Emergency phone',
  'wizard.summary.language': 'Language',
  'wizard.summary.shirt': 'T-shirt size',
  'wizard.summary.fitness': 'Fitness',
  // fitness_self choice step (BOARDROOM-2026-09-24-193944): the QUESTION text is
  // organiser-authored (config.fitnessSelf.prompt); this note is the standing
  // helper line under it. The level labels are authored too (Gold/Silver/…).
  'wizard.fitness.note': 'Your organiser asks this to place you fairly.',
  'wizard.fitness.labelFallback': 'How would you rate your fitness?',
  // Phone hint templates (libphonenumber-backed expected-count machinery)
  'wizard.phone.hintIdle': '{need} digits after the country code — no leading 0',
  'wizard.phone.hintOk': '✓ Looks right',
  'wizard.phone.hintShort': 'Needs {need} digits — you have {have}',
  // Library-rejected number whose LENGTH is already plausible — a count
  // message would be self-contradictory, so name the real failure.
  'wizard.phone.hintInvalid': 'Doesn’t look like a valid number — check the digits',
  // Save-path error fallback (a thrown non-Error; real Error messages surface
  // verbatim).
  'wizard.save.failed': 'save failed',
  // Load / review notices
  // Hydration hold (CMT-2026-09-03-026): the step body is HELD while the
  // saved profile loads — this status line is what renders instead of the
  // blank fields Duncan hit (an account with a profile never sees an empty
  // stepper).
  'wizard.loadingProfile': 'Loading your saved details…',
  'wizard.loadNote.noSession': "You're not signed in — nothing here will be saved.",
  'wizard.loadNote.loadFail': "Couldn't load your saved profile — starting from what's on this device.",
  'wizard.legacyReview': 'We found your details from your previous Trumin account. Please review them and make any changes before you save.',
  // Legacy marry-up × identity lock (review P1-1b): at level <= 2 the standard
  // banner would invite edits over DISABLED identity fields. This variant
  // scopes the review ask to the still-editable details. Arabic arrives via
  // the ar.ts spread as an UNTRANSLATED placeholder pending a translator +
  // native reviewer (PRODUCT-RULES §Localisation — never machine-translated).
  'wizard.legacyReview.identityLocked': 'We found details from your previous Trumin account. Your name, date of birth and gender were verified at event check-in and stay as they are — please review the rest and make any changes before you save.',
  // Identity-field edit lock (PROGRAMME-RULE-IDENTITY-FIELD-EDIT-LOCK-BY-
  // VERIFICATION-LEVEL-V1): shown on the name + DOB/gender steps when the
  // account's verification level is <= 2 (event-ID-verified). The Arabic value
  // arrives via the ar.ts spread as an UNTRANSLATED placeholder pending a
  // translator + native reviewer (PRODUCT-RULES §Localisation — never
  // machine-translated; flagged in the WU return).
  'wizard.identityLocked.note': 'These details were verified at event check-in and can no longer be edited here. Contact support to correct them.',

  // -------------------------------------------------------------------------
  // Waiver flow (V1.9.1 Phase 4) — handoff README §The flows → C. Waiver.
  // CHROME strings only: the documents / sections / clauses / consent
  // sentences are ORGANISER data off the settings wire and are NEVER platform-
  // translated (PRODUCT-RULES §Localisation — per-locale organiser content).
  // -------------------------------------------------------------------------
  // The self-subject named party in the doc intro ({party} hole) — the
  // guardian variant carries the child's name instead.
  'waiver.party.you': 'you',
  // Submit-path error fallback (a thrown non-Error; real messages verbatim).
  'waiver.submitFailed': 'waiver submit failed',
  // my-api 422 guardian_signature_required (item 1 on #241): an under-18
  // participant can't sign their own waiver — a clear next step, never a dead end.
  'waiver.guardianRequired.title': 'A parent or guardian needs to sign this waiver',
  'waiver.guardianRequired.body': 'You’re under 18 on race day, so you can’t sign your own waiver. Please ask the person who bought this ticket to reassign your place using “Under 18” — a parent or guardian will then be asked to sign for you.',
  'waiver.guardianRequired.back': 'Back to Get ready',
  'waiver.eyebrow.doc': 'Document {n} of {m} · {version}',
  'waiver.eyebrow.sign': 'Your signature',
  'waiver.eyebrow.signature': 'Signature',
  'waiver.namedParty': 'NAMED PARTY',
  'waiver.stepnum.signed': 'SIGNED',
  // Live gate above the sections (README §C: `4 sections still to accept` →
  // `✓ All required sections accepted`).
  // Wizard advisory-bar seat identity (BOARDROOM-2026-09-09-183201 item 2:
  // the anonymous-banner incident — a foreign holding's mismatch must NAME
  // the seat it refers to, never render as bare copy).
  'wizard.elig.seatContext': '{copy} ({ticket} · {event})',
  'wizard.elig.eventContext': '{copy} ({event})',
  'waiver.gate.left.one': '1 section still to accept',
  'waiver.gate.left.other': '{n} sections still to accept',
  'waiver.gate.ok': '✓ All required sections accepted',
  'waiver.gate.signed': 'Signed by {name} · {when} — read-only',
  // Section tags (three states — BOARDROOM-2026-08-15-082717: a
  // read-then-sign clause is never "optional").
  'waiver.tag.must': 'MUST ACCEPT',
  'waiver.tag.optional': 'OPTIONAL',
  'waiver.tag.signed': 'ACCEPTED BY SIGNING',
  // Default tick label when a REQUIRED section carries no organiser-authored
  // consent copy (P1 BOARDROOM-2026-09-09-162628: the V5 resolver ships
  // acknowledgement_required with no consent text — the tick must still
  // render or the accept gate is unsatisfiable).
  'waiver.consent.default': 'I have read and accept this section',
  'waiver.clauses.hide': 'Hide clauses',
  'waiver.clauses.show': 'Show {n} clauses',
  // Doc intros ({party} = "you" or the child's name on the guardian variant;
  // {org} = organiser display name from the resolved template).
  'waiver.intro.doc': 'Between {party} and {org}. Accept each section to continue — Trumin keeps the signed record.',
  'waiver.intro.signedDoc': 'Between {party} and {org}. This is the agreement as you accepted it — nothing here can be changed.',
  // Sign step (guardian signs THEIR OWN name — README §C guardian variant).
  'waiver.sign.heading.self': 'Sign to finish',
  'waiver.sign.heading.guardian': 'Sign as guardian',
  'waiver.sign.intro.self': 'Your typed name is your legal signature.',
  'waiver.sign.intro.guardian': "You are signing as {who}'s parent or legal guardian.",
  'waiver.sign.for.self': 'Signing as {who}',
  'waiver.sign.for.guardian': 'Signing for {who} · Guardian',
  'waiver.sign.label.self': 'Type your full name',
  'waiver.sign.label.guardian': 'Type your full name (the guardian)',
  'waiver.sign.hint.must': 'Must match {name} exactly',
  'waiver.sign.hint.ok': '✓ Signature matches your profile name',
  'waiver.sign.hint.bad': "Doesn't match {name}",
  'waiver.sign.hint.captured': 'Signature captured',
  // Sign-box instruction (Duncan ruling BOARDROOM-2026-09-10-192803 / CMT-037
  // bug 4): the box is EMPTY; the expected signer's name lives HERE, below it.
  'waiver.sign.instruction': 'Type your full name \u201c{name}\u201d to sign.',
  'waiver.sign.instructionNoName': 'Type your full name to sign.',
  'waiver.sign.note': 'A copy is emailed to you and kept in your Trumin account. Your signing time and device are recorded with it.',
  'waiver.sign.cta': 'Sign & submit',
  'waiver.sign.busy': 'Signing…',
  // Blocked-reason bar (README blocked-reason contract — names the SPECIFIC
  // missing thing, above the footer button).
  'waiver.block.doc.one': 'Accept the 1 remaining required section to continue',
  'waiver.block.doc.other': 'Accept the {n} remaining required sections to continue',
  'waiver.block.sign.empty': 'Type your full name to sign',
  'waiver.block.sign.match': 'Your signature must match {name} exactly',
  // Profile gate (signing needs a profile name to validate against).
  'waiver.profileGate.note': 'Complete your profile before signing your waiver — your typed signature has to match your profile name.',
  'waiver.profileGate.cta': 'Complete your profile',
  // Receipt (README §C: every document with its version, declined optionals,
  // re-read + download; header reads SIGNED).
  'waiver.receipt.title': 'Waiver signed',
  'waiver.receipt.stamp': 'Signed {when} · timestamp and device recorded',
  'waiver.receipt.k.documents': 'Documents',
  'waiver.receipt.k.participant': 'Participant',
  'waiver.receipt.k.signedBy': 'Signed by',
  'waiver.receipt.k.signedByGuardian': 'Signed by (guardian)',
  'waiver.receipt.k.signedAt': 'Signed at',
  'waiver.receipt.k.optional': 'Optional consents',
  'waiver.receipt.declined': 'Declined: {list}',
  'waiver.receipt.allAccepted': 'All accepted',
  'waiver.receipt.download': 'Download signed copy (PDF)',
  'waiver.receipt.reread': 'Re-read the agreement',
  'waiver.receipt.next': 'Next: {label}',
  'waiver.receipt.viewTicket': 'View your ticket',
  'waiver.receipt.pending': 'You’re all set — nothing more for you to do. Your ticket will appear in your wallet once it’s issued.',
  'waiver.signedOn': 'Signed on {date} at {time}',
  'waiver.signedOnBare': 'Signed on {when}',
  // Footer / navigation
  'waiver.backToGetReady': 'Back to Get ready',
  'waiver.backToReceipt': 'Back to receipt',
  // Loading / empty / error states (honest — never invented documents).
  'waiver.loading': 'Loading the waiver…',
  'waiver.fallbackContext': 'Waiver',
  'waiver.empty.title': 'This event’s waiver isn’t available yet',
  'waiver.empty.body': 'The organiser hasn’t published waiver documents to Trumin for this event yet — there’s nothing to sign here for now.',
  'waiver.error.title': 'We couldn’t load this waiver',
  'waiver.error.body': 'The waiver couldn’t be reached just now — this is a loading problem, not a missing document. Please try again in a moment.',
  'waiver.error.retry': 'Try again',
  'waiver.profileLoading': 'Loading your profile…',

  // -------------------------------------------------------------------------
  // Heat selection (V1.9.1 Phase 4) — handoff README §The flows → D. Heat
  // selection. Heats belong to a ticket type, served by manage.trumin via the
  // existing my-api read; option content (times, capacities) is wire data.
  // -------------------------------------------------------------------------
  'heat.heading.choose': 'Choose your heat',
  'heat.heading.change': 'Change your heat',
  'heat.intro.choose': 'Choose a start time for {type}. Heats fill as people choose, so the list is live.',
  'heat.intro.chooseNoType': 'Choose a start time. Heats fill as people choose, so the list is live.',
  'heat.intro.change': 'You are in the {time} heat. Pick another heat to move — your place is held until you confirm.',
  'heat.intro.changeNoTime': 'Pick another heat to move — your place is held until you confirm.',
  'heat.count': '{open} of {total} heats still open',
  'heat.pill.full': 'FULL',
  'heat.pill.left': '{left} OF {cap} LEFT',
  'heat.row.tbc': 'Start time to be confirmed',
  'heat.note': 'Heats are set by the organiser and shown in event time. You can change your heat until entries close.',
  'heat.block': 'Choose an available heat to continue',
  'heat.confirm': 'Confirm heat',
  'heat.confirm.move': 'Move to this heat',
  'heat.team.first': 'You’re choosing the heat for your whole team — once set it locks for everyone.',
  'heat.locked.kicker': 'YOUR TEAM’S HEAT',
  'heat.locked.note': 'Your team’s heat is already set — everyone in your team races together, so it can’t be changed here.',
  'heat.empty': '{event} hasn’t published a heat list yet — nothing to choose from.',
  'heat.loading': 'Loading available heats…',
  'heat.error': 'Couldn’t load the heat list right now — try again shortly.',
  // Assign-path error fallback (a thrown non-Error; real messages verbatim).
  'heat.assignFailed': 'heat assignment failed',

  // -------------------------------------------------------------------------
  // Event questionnaire (SETTINGS-2026-08-25-105204 via the my-api BFF).
  // CHROME only — questions/options are organiser wire data, never platform-
  // translated here.
  // -------------------------------------------------------------------------
  'quest.head.title': 'Event questionnaire',
  'quest.head.sub': 'A few quick questions from the organiser — your answers help them plan the event.',
  'quest.loading': 'Loading the questionnaire…',
  'quest.loadError': 'Couldn’t load the questionnaire right now — try again shortly.',
  'quest.retry': 'Retry',
  'quest.inactive': '{event} has no questionnaire open right now.',
  'quest.submitted': '✓ Questionnaire completed — thanks, your answers are in.',
  'quest.required.one': '1 required question left to answer',
  'quest.required.other': '{n} required questions left to answer',
  'quest.submitError': 'Couldn’t save your answers right now — nothing was marked complete. Try again shortly.',
  'quest.submit': 'Submit answers',
  'quest.saving': 'Saving…',
  // Searchable choice questions (settings PR#310 type-ahead render)
  'quest.search.placeholder': 'Type to search…',
  'quest.search.noMatch': 'No matches',
  'quest.multi.hint': 'Select all that apply.',
  'quest.chip.remove': 'Remove {label}',
  // Guardian-mode framing (BOARDROOM-2026-09-05-101707): organiser-authored
  // questions render VERBATIM — the portal never rewrites their second-person
  // wording. These keys reframe WHO the answers are about when a parent/
  // guardian answers for a named child (chrome only, like the rest of quest.*).
  'quest.guardian.about': 'About {child}',
  'quest.guardian.title': 'You’re answering on behalf of {child}',
  'quest.guardian.sub': 'The organiser’s questions may say “you” — answer about {child} unless a question is clearly about you as their parent or guardian.',

  // -------------------------------------------------------------------------
  // Profile-time eligibility enforcement (BOARDROOM-2026-08-28-194055).
  // Duncan's plain-English reject copy per axis — VERBATIM the server's
  // enforcementMessage() strings, mapped through the machine `reason` +
  // ticket contract so the wizard pre-save gate and the surfaced 422 read
  // identically. Arabic falls back via the ar spread (no machine translation).
  // -------------------------------------------------------------------------
  'eligibility.reject.gender.male': 'This is a male ticket and must be assigned to a male participant.',
  'eligibility.reject.gender.female': 'This is a female ticket and must be assigned to a female participant.',
  'eligibility.reject.age.band': 'The date of birth given is outside this ticket’s {min}–{max} age range.',
  'eligibility.reject.age.min': 'This ticket is for participants aged {min} or over.',
  'eligibility.reject.age.max': 'This ticket is for participants aged {max} or under.',
  // Honest fallback when a 422 arrives with no mappable contract AND no server
  // message (should not happen — the server always sends one; never blank).
  'eligibility.reject.generic': 'This profile doesn’t meet this ticket’s eligibility rules. Please contact the Help Desk.',

  // -------------------------------------------------------------------------
  // Eligibility-mismatch EXPERIENCE (Duncan rulings BOARDROOM-2026-08-29-120259
  // + ADDENDUM 122136 + ADDENDUM 2 123126). Participant-voice fail-fast copy
  // (event-genderized from the ticket's own contract; the age axis reuses the
  // reason-aware reject keys above — real numbers, never a fabricated band),
  // the truthful step state, and the two support-out sub-states. Arabic falls
  // back via the ar spread (no machine translation).
  // -------------------------------------------------------------------------
  'eligibility.mismatch.gender.male': 'This ticket is for a male participant.',
  'eligibility.mismatch.gender.female': 'This ticket is for a female participant.',
  // Truthful step meta: the profile DATA is complete; the ticket is what
  // doesn't fit. Never "Details missing" (ruling 3).
  'getReady.step.profile.mismatch': 'Complete — not valid for this ticket',
  'getReady.pill.mismatch': 'Ticket not valid',
  // Sub-state 1 — before reporting: the banner carries the out (verbatim
  // button copy per ruling 2).
  'eligibility.support.cta': 'Submit a support ticket so we can help you resolve this',
  // Sub-state 2 — after reporting: honest submitted state + a real reference
  // (ticket id + report date — the facts support can key on; no fabricated
  // case number exists because no server intake seam exists yet).
  'eligibility.support.reported': 'You currently don’t have a valid ticket for this event — support request submitted.',
  'eligibility.support.reportedRef': 'Reference: ticket {ticket} · reported {date}',
  // Low-key re-open link in sub-state 2 (P1-1): the recorded state only ever
  // asserts an ATTEMPTED mailto navigation — a handlerless browser no-ops
  // silently — so the out stays reachable without re-nagging. Arabic rides
  // the ar spread as an untranslated placeholder (same discipline as the
  // other eligibility.support.* keys).
  'eligibility.support.reopen': 'Open the support email again',
  // COMPOSE/CONFIRM interlock (CMT-2026-09-03-021): clicking the support CTA
  // opens the pre-filled email compose and asks the participant to CONFIRM
  // they sent it — the "submitted" record is only ever written on that
  // explicit confirm, never on the bare click (no more instant "support
  // request submitted" over a compose that may have silently no-oped).
  'eligibility.support.composed': 'A pre-filled support email has opened in your email app — send it, then confirm below.',
  'eligibility.support.confirm': 'I’ve sent the support email',

  // -------------------------------------------------------------------------
  // GUARDIAN CLAIM JOURNEY (S2 FE slice, BOARDROOM-2026-09-03-052111 /
  // CMT-2026-09-03-020 — ratified child-flow rulings: the child is named by
  // the CHILD everywhere; the parent/guardian signs FOR the child; QR locked
  // until the guardian signature). Arabic rides the ar spread as untranslated
  // placeholders awaiting a translator + native reviewer (PRODUCT-RULES.md
  // §Localisation — no machine translation).
  // -------------------------------------------------------------------------
  // Claim landing — the parent-sign email CTA lands HERE; the copy leads with
  // the CHILD's context, never the standard self-claim framing.
  'claim.guardian.eyebrow': 'Your child’s ticket',
  'claim.guardian.lead': '{child}, your child, has a ticket for {event}.',
  'claim.guardian.leadNoName': 'Your child has a ticket for {event}.',
  'claim.guardian.frame': 'Claim and manage it as their parent or guardian — you’ll sign their waiver and get their race QR.',
  'claim.guardian.claimed': '{child}’s {type} ticket for {event} is yours to manage. Next: confirm their details, sign their waiver, and their entry QR appears in your account.',
  'claim.guardian.claimedNoName': 'Your child’s {type} ticket for {event} is yours to manage. Next: confirm their details, sign their waiver, and their entry QR appears in your account.',
  // Get-ready card, guardian-claimed seat: the account-holder profile row is
  // NOT bib/results data, and the child-confirm row is the child's identity
  // home (DOB + gender are collected here — the parent is the authority).
  'getReady.step.profile.guardianPending': 'Your details as the account holder',
  'getReady.step.childConfirm.label': 'Confirm {name}’s details',
  'getReady.step.childConfirm.labelNoName': 'Confirm your child’s details',
  'getReady.step.childConfirm.pending': 'Their name, date of birth and gender',
  'getReady.step.childConfirm.done': 'Confirmed',
  // Non-blocking prompt (my-api V25 age-band split): a NO-age-band ticket does
  // not need the child's details to be ready — the row invites, never nags.
  'getReady.step.childConfirm.prompt.label': 'Add {name}’s details',
  'getReady.step.childConfirm.prompt.labelNoName': 'Add your child’s details',
  'getReady.step.childConfirm.prompt.meta': 'Optional for this ticket — their date of birth and gender',
  // Guardian framing of the waiver step (the ruled sentence).
  'getReady.step.waiver.labelGuardian': 'Sign the waiver for {name}’s ticket',
  'getReady.step.waiver.labelGuardianNoName': 'Sign the waiver for your child’s ticket',
  'getReady.step.waiver.guardianMeta': 'You sign as their parent or guardian',
  // WHO-IS-THIS-ABOUT markers (BOARDROOM-2026-09-05-101136 — Duncan's live
  // test: "Your profile" on a For-{child} card read as the CHILD's profile):
  // in guardian mode EVERY step row states its subject. The profile label
  // names the PARENT; rows whose label doesn't already carry the child's name
  // (childConfirm/waiver do natively) wear the generic "(for {name})" suffix.
  'getReady.step.profile.labelGuardian': 'Your profile (you, the parent or guardian)',
  'getReady.step.labelForChild': '{label} (for {name})',
  'getReady.step.labelForChildNoName': '{label} (for your child)',
  // Child-confirm flow screen.
  'childConfirm.eyebrow': 'Parent / guardian',
  'childConfirm.heading': 'Confirm {name}’s details',
  'childConfirm.headingNoName': 'Confirm your child’s details',
  'childConfirm.intro': 'This ticket is theirs — their name goes on the bib and the results. Check it, and add their date of birth and gender.',
  'childConfirm.field.first': 'Child’s first name',
  'childConfirm.field.last': 'Child’s last name (optional)',
  // Progressive-disclosure escape under the contract-narrowed year offer:
  // the honest path for a fresh out-of-band entry (expands to the full list;
  // the mismatch advisory — never a block — then speaks).
  'childConfirm.dob.otherYear': 'My child was born in a different year',
  'childConfirm.block.first': 'Enter the child’s first name',
  'childConfirm.block.dob': 'Enter the child’s date of birth',
  'childConfirm.block.gender': 'Choose the child’s gender',
  'childConfirm.cta': 'Confirm details',
  // Server-backed save (my-api V25 guardian-child): in-flight label, and the
  // retry copy for a network/5xx failure (the details are NOT confirmed).
  'childConfirm.saving': 'Saving…',
  'childConfirm.cta.retry': 'Try again',
  'childConfirm.error.retry': 'We couldn’t save your child’s details just now. Please check your connection and try again.',
  'childConfirm.noRecord': 'This seat’s child details aren’t on this device — open the claim email link on this device, or contact support.',

  // Ticket transfer (PROJ-0078-WU-10; docs/current/DESIGN-TRANSFER-RECIPIENT-
  // STEP-V1.md). Behind VITE_TRANSFER_ENABLED. Error copy is the design
  // note's; the preview rows render the server's `preview` verbatim.
  'transfer.cta': 'Transfer ticket',
  'transfer.drawer.eyebrow': 'Transfer',
  'transfer.drawer.title': 'Transfer this ticket',
  'transfer.drawer.close': 'Close transfer',
  'transfer.recipient.label': 'Recipient’s email',
  'transfer.recipient.help': 'They’ll get an email to claim the ticket.',
  'transfer.recipient.continue': 'Continue',
  'transfer.recipient.err.empty': 'Enter the recipient’s email address',
  'transfer.recipient.err.format': 'Enter a valid email address',
  'transfer.recipient.err.self': 'That’s your own email — enter the person you’re transferring to',
  'transfer.submitting': 'Reserving the transfer…',
  'transfer.preview.to': 'Transferring to {email}',
  'transfer.preview.row.delta': 'Price difference',
  'transfer.preview.row.fee': 'Transfer fee',
  'transfer.preview.row.processing': 'Processing fee',
  'transfer.preview.row.total': 'Total',
  'transfer.preview.countdown': 'Reserved for {time}',
  'transfer.preview.confirm': 'Confirm transfer',
  'transfer.preview.paidSoon': 'Card payment for transfers is coming soon',
  'transfer.confirming': 'Confirming the transfer…',
  'transfer.done.sent': 'Transfer sent — {email} will get an email',
  'transfer.done.pending': 'Your transfer is confirmed and will complete shortly',
  'transfer.done.close': 'Done',
  'transfer.lapsed': 'Your reservation lapsed — start again',
  'transfer.restart': 'Start again',
  'transfer.err.unavailable': 'Transfers aren’t available yet',
  'transfer.err.notAllowed': 'You can’t transfer this ticket',
  'transfer.err.ruleDenied': 'Transfers aren’t allowed for this ticket',
  'transfer.err.notActive': 'This ticket can’t be transferred right now',
  'transfer.err.holdPending': 'A transfer for this ticket is already in progress — try again once it lapses',
  'transfer.err.auth': 'Your session has expired — sign in again',
  'transfer.err.authCta': 'Sign in again',
  'transfer.err.linkInvalid': 'This transfer link isn’t valid — use the link from your most recent email',
  'transfer.err.linkExpired': 'This transfer link has expired',
  'transfer.err.invalid': 'We couldn’t start this transfer — try again',
  'transfer.err.unknown': 'Something went wrong — try again or',
  'transfer.err.unknownNoSupport': 'Something went wrong — try again',
  'transfer.contactSupport': 'Contact support',
  'transfer.landing.title': 'Ticket transfer',
  'transfer.landing.body': 'Your transfer link is valid — transfers via link open soon. Keep this email; the link will work once they do.',
} as const

export type MessageKey = keyof typeof en
